import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { useAuth } from "../../../providers/AuthProvider";
import { LotMapSection } from "../components/LotMapSection";
import { listWorkspaces, type Workspace } from "../../workspaces/api/workspaces";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { useResponsiveBreakpoint } from "../../../hooks/useResponsiveBreakpoint";
import { SquaresFourIcon } from "@phosphor-icons/react";
import { iconLibrary, summaryIconProps } from "../../shared/ui/icons";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("AllLotsMapPage");

const pageStackClasses = "flex flex-col gap-8";
const actionsStackClasses = "flex flex-wrap items-center gap-3";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const summaryGridClasses = "grid gap-4 sm:grid-cols-2 lg:grid-cols-4";
const summaryCardClasses =
  "flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 shadow-inner shadow-white/40 dark:border-slate-800 dark:bg-slate-800/50 dark:shadow-none";
const summaryIconWrapperClasses =
  "flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-slate-700 shadow dark:bg-slate-700 dark:text-slate-300";
const summaryLabelClasses =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400";
const summaryValueClasses = "text-lg font-semibold text-slate-900 dark:text-slate-100";

export function AllLotsMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { isMobile } = useResponsiveBreakpoint();
  const [workspaceNames, setWorkspaceNames] = useState<Record<string, string>>({});
  const [totals, setTotals] = useState<{ visibleArea: number; visibleCount: number }>({
    visibleArea: 0,
    visibleCount: 0,
  });

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      { label: t("workspaces.lots.map.all_title") },
    ];
  }, [t]);

  const handleBack =
    useBreadcrumbBack({
      items: breadcrumbItems,
      includeHome: false,
      fallback: { to: "/dashboard" },
    }) ?? (() => navigate(-1));

  useEffect(() => {
    if (!accessToken) return;
    const loadWorkspaceNames = async () => {
      try {
        const workspaces = await listWorkspaces(accessToken);
        if (!Array.isArray(workspaces)) return;
        const map: Record<string, string> = {};
        let totalLots = 0;
        let totalArea = 0;
        (workspaces as Workspace[]).forEach((ws) => {
          if (ws.id) {
            map[ws.id] = ws.name;
          }
          totalLots += ws.agg_active_lots ?? 0;
          totalArea += ws.agg_computed_area_m2 ?? 0;
        });
        setWorkspaceNames(map);
        setTotals({
          visibleArea: totalArea,
          visibleCount: totalLots,
        });
      } catch (err) {
        logger.warn("Failed to load workspace names", err);
      }
    };
    void loadWorkspaceNames();
  }, [accessToken]);

  const snapshotFileBase = useMemo(() => {
    const heading = t("workspaces.lots.map.all_title");
    const slug = heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return slug || "lots-map";
  }, [t]);

  const headers = [
    {
      label: t("workspaces.lots.map.total_lots", "Lots"),
      value: totals.visibleCount.toLocaleString(),
      icon: iconLibrary.lotsActive,
    },
    {
      label: t("workspaces.lots.map.total_area", "Total area"),
      value:
        totals.visibleArea > 0
          ? `${totals.visibleArea.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} m²`
          : "—",
      icon: iconLibrary.lotsArea,
    },
  ];

  return (
    <PageShell width="wide" className={pageStackClasses} showFooter={!isMobile}>
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("workspaces.lots.map.all_title")}
        subtitle={t("workspaces.lots.map.all_subtitle")}
        actions={
          <div className={actionsStackClasses}>
            <button type="button" className={ghostButtonClasses} onClick={handleBack}>
              {t("actions.back")}
            </button>
          </div>
        }
      />

      <LotMapSection
        accessToken={accessToken}
        workspaceNames={workspaceNames}
        snapshotFileBase={snapshotFileBase}
        mapIdPrefix="all-lots-map"
        onLotNavigate={(workspaceId, lotId) =>
          navigate(`/workspaces/${workspaceId}/lots/${lotId}`, {
            state: { fromMap: true },
          })
        }
      />

      <section className={summaryGridClasses}>
        {headers.map((header) => {
          const Icon = header.icon ?? SquaresFourIcon;
          return (
            <article key={header.label} className={summaryCardClasses}>
              <span className={summaryIconWrapperClasses}>
                <Icon {...summaryIconProps} aria-hidden />
              </span>
              <div className="flex flex-col">
                <span className={summaryLabelClasses}>{header.label}</span>
                <strong className={summaryValueClasses}>{header.value}</strong>
              </div>
            </article>
          );
        })}
      </section>
    </PageShell>
  );
}
